// Datos de Mercado Libre ya guardados por el poller (delivery_ml_data) que
// hasta ahora no se mostraban en ningún lado del detalle de Delivery. Panel
// colapsable: no pide /ml-data hasta que el usuario lo abre, y no vuelve a
// pedirlo si ya lo tiene (ver "Preguntas pendientes" del informe "LYNX — ML
// Poller & Turbo Audit" / "Turbo Promise & Control Tower Intelligence").
//
// Importante, por diseño: la "promesa de entrega" que se muestra acá es la
// que persiste shipmentData.js desde el shipment principal de ML
// (estimated_delivery_date/from/to) -- Lynx NUNCA consulta el recurso
// /lead_time de ML (confirmado por código, sin llamadas a ese endpoint en
// server/utils/mercadolibre.js), así que no se puede afirmar que esta fecha
// sea el deadline oficial de cumplimiento de ML (estimated_delivery_final,
// en ese otro recurso) -- puede ser solo la estimación mostrada al
// comprador. Y nunca es el deadline interno de Turbo
// (turbo_dispatches.deadline_at), que ese mismo informe encontró que Lynx
// calcula sin leer este campo. Por eso este panel muestra la fecha cruda,
// con su fuente aclarada, y deliberadamente no calcula "a tiempo/vencido".
import React, { useEffect, useState } from "react";
import { Clock, Package, Hash, Printer, ChevronRight, ChevronDown, Copy, Check, MapPin, Truck, Receipt } from "lucide-react";
import { authFetch } from "../../../lib/authFetch";

type MlData = {
  weight?: number | string | null;
  dimensions?: string | null;
  items_quantity?: number | null;
  estimated_delivery_date?: string | null;
  estimated_delivery_from?: string | null;
  estimated_delivery_to?: string | null;
  tracking_number?: string | null;
  logistic_type?: string | null;
  date_first_printed?: string | null;
  shipping_cost?: number | string | null;
  declared_value?: number | string | null;
  geolocation_type?: string | null;
};

type FetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "empty" } // delivery no proviene de ML
  | { status: "forbidden" }
  | { status: "error"; message: string }
  | { status: "ok"; data: MlData };

const LOGISTIC_TYPE_LABEL: Record<string, string> = {
  self_service: "Flex (self_service)",
  cross_docking: "Cross docking",
  fulfillment: "Fulfillment",
  drop_off: "Drop off",
};

function fechaArt(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (
    new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Argentina/Buenos_Aires",
    }).format(d) + " ART"
  );
}

function pesoKg(weight: number | string | null | undefined) {
  if (weight === null || weight === undefined || weight === "") return null;
  const n = typeof weight === "string" ? Number(weight) : weight;
  if (!Number.isFinite(n) || n <= 0) return null;
  // delivery_ml_data.weight viene en gramos (comentario en server/sql).
  return `${(n / 1000).toLocaleString("es-AR", { maximumFractionDigits: 2 })} kg`;
}

// "35.0x30.0x26.0" -> "35 × 30 × 26 cm". Puede venir más de un grupo separado
// por " | " (uno por ítem distinto del paquete) -- se formatea cada grupo
// por separado, preservando el orden original de cada dimensión (no se
// asume que sea alto/ancho/largo en ningún orden particular).
function formatDimensiones(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const grupos = raw.split("|").map((g) => g.trim()).filter(Boolean);
  if (!grupos.length) return null;
  const formateados = grupos.map((g) => {
    const partes = g.split(/x/i).map((p) => p.trim()).filter(Boolean);
    if (partes.length < 2) return g;
    const nums = partes.map((p) => {
      const n = Number(p);
      return Number.isFinite(n) ? String(n) : p;
    });
    return `${nums.join(" × ")} cm`;
  });
  return formateados.join(" · ");
}

function money(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("es-AR", { style: "currency", currency: "ARS" });
}

const ICON_SIZE = 15;
const ICON_MUTED: React.CSSProperties = { color: "var(--text-secondary)", flexShrink: 0 };

const boxStyle: React.CSSProperties = {
  gridColumn: "1 / -1",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "var(--surface-alt)",
  padding: "14px",
};
const labelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const mutedStyle: React.CSSProperties = { color: "var(--text-secondary)", fontSize: "13px" };
const toggleButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  fontSize: "13px",
  padding: 0,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};
const promesaCardStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  border: "1px solid var(--border)",
  borderRadius: "12px",
  padding: "12px 14px",
  background: "var(--surface)",
  marginTop: "10px",
};
const statRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginTop: "10px",
};
const statTileStyle: React.CSSProperties = {
  flex: "1 1 120px",
  minWidth: "110px",
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  border: "1px solid var(--border)",
  borderRadius: "12px",
  padding: "10px 12px",
  background: "var(--surface)",
};
const identGroupStyle: React.CSSProperties = {
  marginTop: "10px",
  border: "1px solid var(--border)",
  borderRadius: "12px",
  padding: "10px 12px",
  background: "var(--surface)",
};
const identRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  flexWrap: "wrap",
};
const copyButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: "8px",
  padding: "3px 7px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "12px",
  color: "var(--text-secondary)",
};
const complementaryGridStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginTop: "10px",
};

function StatTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div style={statTileStyle}>
      <span style={ICON_MUTED}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={labelStyle}>{label}</div>
        <div>{value}</div>
      </div>
    </div>
  );
}

export default function DeliveryMlDataPanel({
  deliveryId,
  token,
}: {
  deliveryId: number;
  token: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showComplementary, setShowComplementary] = useState(false);
  const [state, setState] = useState<FetchState>({ status: "idle" });
  const [copiado, setCopiado] = useState(false);

  // Si el usuario pasa a otro Delivery con el modal ya abierto, no hay que
  // seguir mostrando los datos del anterior -- pero tampoco hay que volver a
  // pedir nada hasta que lo abra de nuevo (misma lógica lazy de siempre).
  useEffect(() => {
    setExpanded(false);
    setShowComplementary(false);
    setState({ status: "idle" });
    setCopiado(false);
  }, [deliveryId]);

  useEffect(() => {
    if (!expanded || state.status !== "idle") return;
    let vivo = true;
    setState({ status: "loading" });
    authFetch(`/v2/deliveries/${deliveryId}/ml-data`, { token })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (r.status === 403) return { kind: "forbidden" as const };
        if (r.status === 404) return { kind: "empty" as const };
        if (!r.ok || j?.ok === false) throw new Error(j?.error || `Error ${r.status}`);
        return { kind: "ok" as const, data: (j.ml_data ?? {}) as MlData };
      })
      .then((res) => {
        if (!vivo) return;
        if (res.kind === "forbidden") setState({ status: "forbidden" });
        else if (res.kind === "empty") setState({ status: "empty" });
        else setState({ status: "ok", data: res.data });
      })
      .catch((e) => {
        if (vivo) setState({ status: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, deliveryId, token]);

  const copiarTracking = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      // Clipboard puede no estar disponible (sin permiso, contexto no seguro);
      // no rompe nada, el dato sigue visible para copiar a mano.
    }
  };

  return (
    <div style={boxStyle}>
      <button type="button" onClick={() => setExpanded((v) => !v)} style={toggleButtonStyle} aria-expanded={expanded}>
        {expanded ? <ChevronDown size={ICON_SIZE} style={ICON_MUTED} /> : <ChevronRight size={ICON_SIZE} style={ICON_MUTED} />}
        <span style={labelStyle}>Información Mercado Libre</span>
      </button>

      {!expanded ? null : (
        <div>
          {state.status === "loading" ? <div style={{ ...mutedStyle, marginTop: "10px" }}>Cargando…</div> : null}
          {state.status === "error" ? (
            <div style={{ color: "var(--danger)", marginTop: "10px" }}>{state.message}</div>
          ) : null}
          {state.status === "forbidden" ? (
            <div style={{ ...mutedStyle, marginTop: "10px" }}>No tenés permiso para ver los datos de Mercado Libre de este envío.</div>
          ) : null}
          {state.status === "empty" ? (
            <div style={{ ...mutedStyle, marginTop: "10px" }}>Este envío no proviene de Mercado Libre (o todavía no tiene datos guardados).</div>
          ) : null}

          {state.status === "ok" ? (
            <>
              <div style={promesaCardStyle}>
                <Clock size={18} style={{ color: "var(--accent, var(--text-secondary))", flexShrink: 0, marginTop: "1px" }} />
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Promesa de entrega</div>
                  <div style={{ fontSize: "16px", fontWeight: 600 }}>{fechaArt(state.data.estimated_delivery_date) ?? "—"}</div>
                  {state.data.estimated_delivery_from || state.data.estimated_delivery_to ? (
                    <div style={mutedStyle}>
                      Ventana: {fechaArt(state.data.estimated_delivery_from) ?? "—"} a {fechaArt(state.data.estimated_delivery_to) ?? "—"}
                    </div>
                  ) : null}
                  <div style={{ ...mutedStyle, marginTop: "4px" }}>
                    Promesa mostrada por Mercado Libre al comprador — no es el deadline interno de Turbo.
                  </div>
                </div>
              </div>

              <div style={statRowStyle}>
                <StatTile icon={<Package size={ICON_SIZE} />} label="Peso declarado" value={pesoKg(state.data.weight) ?? "—"} />
                <StatTile icon={<Package size={ICON_SIZE} />} label="Dimensiones" value={formatDimensiones(state.data.dimensions) ?? "—"} />
                <StatTile icon={<Truck size={ICON_SIZE} />} label="Ítems" value={state.data.items_quantity ?? "—"} />
              </div>
              <div style={{ ...mutedStyle, marginTop: "6px" }}>
                Peso y dimensiones son los declarados por el vendedor en Mercado Libre, no una medición física del paquete.
              </div>

              <div style={identGroupStyle}>
                <div style={labelStyle}>Identificación</div>
                <div style={{ ...identRowStyle, marginTop: "6px" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Hash size={ICON_SIZE} style={ICON_MUTED} />
                    {state.data.tracking_number || "—"}
                  </span>
                  {state.data.tracking_number ? (
                    <button type="button" style={copyButtonStyle} onClick={() => copiarTracking(state.data.tracking_number as string)}>
                      {copiado ? <Check size={13} /> : <Copy size={13} />}
                      {copiado ? "Copiado" : "Copiar"}
                    </button>
                  ) : null}
                </div>
                <div style={{ ...identRowStyle, marginTop: "6px" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Truck size={ICON_SIZE} style={ICON_MUTED} />
                    {state.data.logistic_type ? LOGISTIC_TYPE_LABEL[state.data.logistic_type] ?? state.data.logistic_type : "—"}
                  </span>
                </div>
                <div style={{ ...mutedStyle, display: "flex", alignItems: "center", gap: "6px", marginTop: "6px" }}>
                  <Printer size={13} />
                  Etiqueta impresa: {fechaArt(state.data.date_first_printed) ?? "—"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowComplementary((v) => !v)}
                style={{ ...toggleButtonStyle, marginTop: "12px" }}
                aria-expanded={showComplementary}
              >
                {showComplementary ? <ChevronDown size={ICON_SIZE} style={ICON_MUTED} /> : <ChevronRight size={ICON_SIZE} style={ICON_MUTED} />}
                <span style={labelStyle}>Información complementaria</span>
              </button>
              {showComplementary ? (
                <div style={complementaryGridStyle}>
                  <StatTile icon={<Receipt size={ICON_SIZE} />} label="Costo de envío ML" value={money(state.data.shipping_cost) ?? "—"} />
                  <StatTile icon={<Receipt size={ICON_SIZE} />} label="Valor declarado" value={money(state.data.declared_value) ?? "—"} />
                  <StatTile icon={<MapPin size={ICON_SIZE} />} label="Calidad de geolocalización" value={state.data.geolocation_type || "—"} />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
